import { FC, useEffect, useRef, useState } from 'react'
import { Stack, TextField } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useCameraIdToSid, useSystemNumberToSidFn } from '../../api/apiQueries'
import LoadingButton from '@mui/lab/LoadingButton'
import { useParams } from 'react-router-dom'
import { isUuid, LOCATION_SEARCH_INPUT_PATTERN, SID_OR_SYSTEM_NUMBER_PATTERN } from '../../utils/utils'
import { LoggerName, namedLogger } from '../../utils/logger'

const logger = namedLogger(LoggerName.LocationSearch)

export type LocationSearchProps = {
  onSearchChange: (sid: string, cameraId?: string) => void
  shouldAllowCameraIdSearch?: boolean
  onError?: (error: unknown) => void
}

type Form = {
  searchInput: string
}

/**
 * SearchPanel component.
 * Renders a search panel with an input field and a search button.
 */
export const LocationSearch: FC<LocationSearchProps> = ({ onSearchChange, shouldAllowCameraIdSearch, onError }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { sid: locationId } = useParams()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<Form>({
    defaultValues: { searchInput: locationId }
  })

  // we want to update the input field if the locationId in the URL changes, but only if it's different from the current value in the input field to avoid overwriting user input
  const lastLocationIdRef = useRef(locationId)
  useEffect(() => {
    if (locationId && lastLocationIdRef.current !== locationId) {
      setValue('searchInput', locationId)
      lastLocationIdRef.current = locationId
    }
  }, [locationId, setValue])

  const translateSystemNumberToSid = useSystemNumberToSidFn()
  const translateCameraIdToSid = useCameraIdToSid()

  function isSystemNumber(inputtedValue: string | number): boolean {
    if (typeof inputtedValue === 'string') {
      return inputtedValue.toUpperCase().startsWith('SSI')
    }
    return false
  }

  const onSubmit = async (form: Form) => {
    let sid = form.searchInput
    let isUuidInput = false
    let shouldCallOnSearchChange = true

    try {
      if (shouldAllowCameraIdSearch && isUuid(form.searchInput)) {
        isUuidInput = true
        setIsLoading(true)
        const translated = await translateCameraIdToSid(form.searchInput)
        sid = translated.locationId

        // If there's a camera ID, use that
        if (onSearchChange) {
          onSearchChange(sid, form.searchInput)
          setIsLoading(false)
          return
        }
      } else if (isSystemNumber(form.searchInput)) {
        setIsLoading(true)

        try {
          const translated = await translateSystemNumberToSid(form.searchInput)
          sid = translated.locationId
        } catch (e) {
          onError?.(e)
          shouldCallOnSearchChange = false
          setValue('searchInput', '')
          lastLocationIdRef.current = undefined
          // The lookup failed and the user can't resolve their search - surfaced via onError, so warn.
          logger.error('Failed to translate system number to SID', e as Error)
        }
      }
    } catch (e) {
      // Defensive catch so an unexpected parsing error doesn't leak out; the flow continues, so info.
      logger.error('Ignoring non-fatal error while resolving search input', e as Error)
      isUuidInput = false
    } finally {
      if (shouldCallOnSearchChange) {
        onSearchChange(sid, isUuidInput ? form.searchInput : undefined)
      }

      setIsLoading(false)
    }
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      data-testid="searchPanelForm"
      direction="row"
      spacing="var(--spacing-l)"
      alignItems="end"
      marginTop="var(--spacing-3xs)"
      useFlexGap
    >
      <TextField
        id="searchInput"
        label={shouldAllowCameraIdSearch ? 'SID or Camera ID' : 'SID or System Number'}
        error={!!errors.searchInput}
        helperText={errors.searchInput?.message}
        autoComplete={'off'}
        slotProps={{
          htmlInput: {
            // this turns off onepassword and lastpass autofill
            'data-1p-ignore': true,
            'data-lpignore': true,
            'data-testid': 'sid'
          }
        }}
        sx={{ inlineSize: '210px' }}
        {...register('searchInput', {
          required: 'SID or System Number is required',
          pattern: shouldAllowCameraIdSearch
            ? {
                value: LOCATION_SEARCH_INPUT_PATTERN,
                message: 'Must be numeric, start with SSI, or be a valid UUID'
              }
            : { value: SID_OR_SYSTEM_NUMBER_PATTERN, message: 'Must be numeric or start with SSI' }
        })}
      />
      <LoadingButton loading={isLoading} variant={'contained'} color={'primary'} type="submit" data-testid="searchButton">
        Search
      </LoadingButton>
    </Stack>
  )
}
